import { useQuery, UseQueryResult } from '@tanstack/react-query';
import siteBatchService from '../services/SiteBatchService';
import { SiteBatch } from '../models/SiteBatch';

const POLLING_INTERVAL_MS = 5_000;

export const activeBatchesQueryKey = (projectId: string | null) =>
  ['site-batch', 'active-all', projectId] as const;

interface UseActiveBatchesOptions {
  /** Pause polling when the site management view is unmounted. */
  enabled: boolean;
}

/**
 * Poll the list of running/pending batches every 5s while the site
 * management view is open. Used to decorate site rows with "배포 중"
 * badges and progress info without the user having to open each modal.
 *
 * The `projectId` param is required by the backend path but the query
 * itself is cross-site; any valid project id works. We pass whatever
 * site id the view currently has handy.
 */
export function useActiveBatches(
  projectId: string | null,
  options: UseActiveBatchesOptions,
): UseQueryResult<SiteBatch[], Error> {
  const { enabled } = options;

  return useQuery<SiteBatch[], Error>({
    queryKey: activeBatchesQueryKey(projectId),
    queryFn: () => siteBatchService.listActiveBatches(projectId as string),
    enabled: enabled && !!projectId,
    refetchInterval: () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      return POLLING_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    retry: 2,
    staleTime: 1_000,
  });
}
